import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Copy, Check, ArrowRight, ShieldCheck, Mail, Truck, ExternalLink, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { statusPillClass, statusLabel } from "../data/orderStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZELLE_EMAIL = "luviarx@gmail.com";
const ZELLE_NAME = "LuviaRx LLC";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const stripeSessionId = searchParams.get("session_id");
  const stripeOutcome = searchParams.get("stripe"); // "success" | "cancel"
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/orders/${orderNumber}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!cancelled) setOrder(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load order"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderNumber, token]);

  // Poll the Stripe status endpoint until the webhook (or the retrieve
  // fallback inside it) marks the session as paid or pending. Bail out after
  // ~30s so users aren't stuck spinning if the webhook never lands.
  useEffect(() => {
    if (!stripeSessionId || stripeOutcome !== "success") return;
    let cancelled = false;
    let ticks = 0;
    const tick = async () => {
      if (cancelled) return;
      ticks += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${stripeSessionId}`);
        if (data?.status && data.status !== order?.status) {
          // Refetch the full order envelope so any downstream state (shipping,
          // tracking, etc.) reflects the new status too.
          try {
            const { data: fresh } = await axios.get(
              `${API}/orders/${orderNumber}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!cancelled) setOrder(fresh);
          } catch { /* keep polling; transient */ }
        }
        if (["paid", "pending", "failed", "expired"].includes(data?.payment_status)) return;
      } catch { /* keep polling */ }
      if (ticks < 15 && !cancelled) setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [stripeSessionId, stripeOutcome, orderNumber, token, order?.status]);

  const copy = async (value, field) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch { /* clipboard blocked; silent */ }
  };

  if (loading) {
    return (
      <main className="py-20 text-center text-charcoal-400">Loading order…</main>
    );
  }

  if (error || !order) {
    return (
      <main className="py-20">
        <div className="container-x text-center max-w-md mx-auto">
          <h1 className="font-display text-3xl font-bold text-charcoal-600 mb-3">
            Order not found
          </h1>
          <p className="text-charcoal-400 mb-6">{error || "We couldn't find that order on your account."}</p>
          <Link to="/account" className="btn-primary">Go to my account</Link>
        </div>
      </main>
    );
  }

  const isStripe = order.payment_method === "stripe";
  const isAchPending = order.stripe_payment_status === "pending";
  const isStripeCancel = stripeOutcome === "cancel" && isStripe && order.stripe_payment_status !== "paid";
  const headerCopy = (() => {
    if (isStripeCancel) return "You cancelled Stripe checkout — your order is on hold.";
    if (isStripe && isAchPending) {
      return `Your ACH bank transfer is clearing (3-5 business days). We'll email you at ${order.customer_email} the moment it lands.`;
    }
    if (isStripe && order.stripe_payment_status === "paid") {
      return `Payment received. We're preparing your order for shipment — you'll get a tracking email at ${order.customer_email}.`;
    }
    if (isStripe) {
      return `Verifying your payment with Stripe. This usually takes a few seconds — refresh the page if it doesn't update.`;
    }
    return (
      <>Your order will be processed once your Zelle payment is verified.
      This usually takes less than 1 business day. You'll receive an
      update at <span className="text-charcoal-600 font-medium">{order.customer_email}</span>.</>
    );
  })();

  return (
    <main data-testid="order-confirmation-page" className="py-12 md:py-16 bg-cream-200/30">
      <div className="container-x max-w-3xl">
        <div className="text-center mb-10">
          <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-5 ${
            isStripeCancel ? "bg-amber-100" : "bg-sage-100"
          }`}>
            {isStripeCancel ? (
              <AlertTriangle className="w-7 h-7 text-amber-700" strokeWidth={1.8} />
            ) : (
              <CheckCircle2 className="w-7 h-7 text-sage-600" strokeWidth={1.8} />
            )}
          </div>
          <p className="overline mb-2">{isStripeCancel ? "Checkout cancelled" : "Order received"}</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
            {isStripeCancel ? "Payment not completed" : `Thank you, ${order.customer_name || "researcher"}.`}
          </h1>
          <p className="mt-3 text-sm text-charcoal-400 max-w-lg mx-auto">
            {headerCopy}
          </p>
          {isStripeCancel && order.stripe_checkout_url && (
            <a
              href={order.stripe_checkout_url}
              data-testid="stripe-retry-link"
              className="btn-primary mt-6 inline-flex"
            >
              <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
              Resume Stripe Checkout
            </a>
          )}
        </div>

        {/* Order number callout */}
        <div className="surface-card p-6 md:p-8 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1">
                Order number
              </p>
              <p
                data-testid="order-number"
                className="font-display font-bold text-2xl md:text-3xl text-[#8B2252]"
              >
                {order.order_number}
              </p>
              <p className="text-xs text-charcoal-400 mt-1.5">
                Status:{" "}
                <span
                  data-testid="order-status"
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium ${statusPillClass(order.status)}`}
                >
                  {statusLabel(order.status)}
                </span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => copy(order.order_number, "order")}
              data-testid="copy-order-number"
              className="btn-secondary"
            >
              {copiedField === "order"
                ? (<><Check className="w-4 h-4 mr-2" strokeWidth={2} /> Copied</>)
                : (<><Copy className="w-4 h-4 mr-2" strokeWidth={2} /> Copy</>)}
            </button>
          </div>

          <div className="mt-6 bg-cream-200/70 rounded-2xl p-4 md:p-5 text-sm text-charcoal-500 leading-relaxed">
            {isStripe ? (
              <p>
                Total paid via <span className="font-display font-bold text-[#8B2252]">Stripe</span>:{" "}
                <span className="font-display font-bold text-charcoal-600">${order.total.toFixed(2)}</span>.
                {isAchPending
                  ? " Funds will settle in 3-5 business days; we'll email once your bank confirms."
                  : order.stripe_payment_status === "paid"
                  ? " Payment confirmed — order is being prepared for shipment."
                  : " Verifying payment status…"}
              </p>
            ) : (
              <p>
                <span className="font-medium text-charcoal-600">If you haven't already,</span> send{" "}
                <span className="font-display font-bold text-[#8B2252]">${order.total.toFixed(2)}</span>{" "}
                via Zelle to{" "}
                <span className="font-display font-bold text-charcoal-600">{ZELLE_EMAIL}</span> ({ZELLE_NAME}).
                Use <span className="font-medium">{order.order_number}</span> as the memo.
              </p>
            )}
          </div>
        </div>

        {/* Items */}
        <section className="surface-card p-6 md:p-7 mb-6">
          <p className="overline mb-4">Your items</p>
          <div className="divide-y divide-charcoal-100/60">
            {order.items.map((item) => (
              <div
                key={`${item.product_id}-${item.variant_id}`}
                data-testid={`confirmation-row-${item.product_id}-${item.variant_id}`}
                className="py-3 flex items-center justify-between gap-4"
              >
                <div>
                  <p className="font-display font-bold text-charcoal-600">{item.product_name}</p>
                  <p className="text-xs text-charcoal-400 mt-0.5">
                    {item.variant_label} · Qty {item.quantity}
                  </p>
                </div>
                <span className="font-display font-bold text-charcoal-600">
                  ${item.line_total.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Shipping method (selected at checkout) */}
        {order.shipping_method && (
          <section
            data-testid="confirmation-shipping-method"
            className="surface-card p-6 md:p-7 mb-6"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#8B2252]/10 flex items-center justify-center shrink-0">
                <Truck className="w-4 h-4 text-[#8B2252]" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="overline">Shipping method</p>
                  {order.shipping_method === "expedited" && (
                    <span
                      data-testid="confirmation-expedited-badge"
                      className="text-[9px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wider text-white"
                      style={{ background: "#8B2252" }}
                    >
                      Fast Delivery
                    </span>
                  )}
                </div>
                <p className="font-display font-bold text-charcoal-600">
                  {order.shipping_method_label || (order.shipping_method === "expedited" ? "Expedited Shipping" : "Standard Shipping")}
                </p>
                <p className="text-sm text-charcoal-500 mt-0.5">
                  Carrier: <span className="text-charcoal-600 font-medium">{order.shipping_carrier_label || "USPS Priority Mail"}</span>
                </p>
                <p className="text-xs text-charcoal-400 mt-0.5">
                  Estimated delivery: {order.shipping_estimated_days || "1-3 business days"}
                  {" · "}
                  Shipping: {Number(order.shipping_total || 0) === 0 ? "Free" : `$${Number(order.shipping_total || 0).toFixed(2)}`}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Tracking — visible only after admin marks the order Shipped */}
        {order.status === "Shipped / Fulfilled" && order.carrier && order.tracking_number && (
          <section data-testid="confirmation-tracking" className="surface-card p-6 md:p-7 mb-6 border border-emerald-200">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <Truck className="w-4 h-4 text-emerald-700" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1">
                  Tracking
                </p>
                <p className="font-display font-bold text-charcoal-600">
                  {order.carrier} · <span className="select-all">{order.tracking_number}</span>
                </p>
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tracking-link"
                    className="inline-flex items-center text-sm text-[#8B2252] font-medium mt-1 hover:underline"
                  >
                    Track your shipment <ExternalLink className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Shipping address */}
        {order.shipping && (
          <section data-testid="confirmation-shipping" className="surface-card p-6 md:p-7 mb-6">
            <p className="overline mb-3">Shipping to</p>
            <div className="text-sm text-charcoal-600 leading-relaxed">
              <p className="font-display font-bold text-charcoal-600">
                {order.shipping.first_name} {order.shipping.last_name}
              </p>
              <p>{order.shipping.address1}</p>
              {order.shipping.address2 && <p>{order.shipping.address2}</p>}
              <p>
                {order.shipping.city}, {order.shipping.state} {order.shipping.zip_code}
              </p>
              <p className="text-charcoal-400 text-xs mt-2">Phone: {order.shipping.phone}</p>
            </div>
          </section>
        )}

        {/* Summary */}
        <section className="surface-card p-6 md:p-7 mb-8">
          <p className="overline mb-4">Summary</p>
          <div className="space-y-2 text-sm">
            <SummaryRow label="Subtotal" value={`$${order.subtotal.toFixed(2)}`} />
            {order.seasonal_promo?.amount > 0 && (
              <SummaryRow
                testId="confirmation-seasonal-discount"
                label={`${order.seasonal_promo.name} (${order.seasonal_promo.pct}% off)`}
                value={`−$${order.seasonal_promo.amount.toFixed(2)}`}
                color="text-[#8B2252]"
              />
            )}
            {order.volume_discount_pct > 0 && (
              <SummaryRow
                label={`Volume discount (${order.volume_discount_pct}% off)`}
                value={`−$${order.volume_discount_amount.toFixed(2)}`}
                color="text-sage-700"
              />
            )}
            {order.promo_discount_pct > 0 && (
              <SummaryRow
                label={`${order.promo_code} promo (${order.promo_discount_pct}% off)`}
                value={`−$${order.promo_discount_amount.toFixed(2)}`}
                color="text-[#b14a1f]"
              />
            )}
            {order.personal_discount_amount > 0 && (
              <SummaryRow
                testId="confirmation-personal-discount"
                label={`${order.personal_discount_label || "Personal discount"} (${order.personal_discount_pct}% off)`}
                value={`−$${order.personal_discount_amount.toFixed(2)}`}
                color="text-[#8B2252]"
              />
            )}
            {order.shipping_method && (
              <SummaryRow
                testId="confirmation-shipping"
                label={order.shipping_method_label || "Shipping"}
                value={order.shipping_total > 0 ? `$${order.shipping_total.toFixed(2)}` : "Free"}
              />
            )}
            {order.zelle_discount_amount > 0 && (
              <SummaryRow
                testId="confirmation-zelle-discount"
                label={`Zelle payment (${order.zelle_discount_pct}% off)`}
                value={`−$${order.zelle_discount_amount.toFixed(2)}`}
                color="text-sage-700"
              />
            )}
            <div className="h-px bg-charcoal-100 my-2" />
            <div className="flex justify-between items-center">
              <span className="font-display font-bold text-charcoal-600">Total</span>
              <span
                data-testid="confirmation-total"
                className="font-display font-bold text-2xl text-[#8B2252]"
              >
                ${order.total.toFixed(2)} {order.currency}
              </span>
            </div>
          </div>
        </section>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/products" data-testid="confirmation-continue-shopping" className="btn-primary">
            Continue shopping <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Link>
          <Link to="/account" className="btn-secondary">
            <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
            View account
          </Link>
        </div>

        <p className="mt-10 text-[11px] text-charcoal-300 leading-relaxed text-center max-w-xl mx-auto">
          Questions about your order? Reply to your confirmation email or contact us via the FAQ page.
          For Research Use Only. Not for Human Consumption.
        </p>
      </div>
    </main>
  );
}

const SummaryRow = ({ label, value, color, testId }) => (
  <div data-testid={testId} className={`flex justify-between ${color || "text-charcoal-500"}`}>
    <span>{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
